import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Accesso effettuato con successo");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-[58%] relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1673403731025-2f1ac0c042f5?q=85&w=1920&auto=format&fit=crop"
          alt="Italian Villa"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/60 via-stone-900/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-transparent to-stone-900/20" />
        <div className="absolute bottom-0 left-0 right-0 p-16">
          <div className="max-w-lg">
            <div className="w-12 h-[2px] bg-amber-500 mb-8" />
            <h1 className="font-heading text-5xl text-white mb-4 leading-[1.1] tracking-tight">
              La Gestione della Tua Casa
            </h1>
            <p className="text-stone-300 text-base leading-relaxed max-w-sm">
              La piattaforma professionale per la gestione dei tuoi affitti brevi. Monitora prenotazioni, guadagni e proprietà in un unico posto.
            </p>
          </div>
        </div>
        <div className="absolute top-8 left-8">
          <p className="text-[10px] text-white/40 font-mono tracking-[0.3em] uppercase">Property Management</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAFAF9]">
        <div className="w-full max-w-[360px]">
          <div className="lg:hidden mb-12">
            <div className="w-10 h-[2px] bg-amber-500 mb-6" />
            <h1 className="font-heading text-4xl text-stone-900 tracking-tight">La Gestione della Tua Casa</h1>
            <p className="text-stone-400 mt-2 text-sm">Property Management</p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading text-2xl text-stone-900 tracking-tight">Bentornato</h2>
            <p className="text-stone-400 text-sm mt-2">Inserisci le tue credenziali per accedere</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-stone-600 text-xs font-medium uppercase tracking-wider">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Il tuo username"
                className="mt-2 h-12 bg-white border-stone-200 focus:border-stone-400 focus:ring-stone-400/20 rounded-sm text-sm"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-stone-600 text-xs font-medium uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="La tua password"
                className="mt-2 h-12 bg-white border-stone-200 focus:border-stone-400 focus:ring-stone-400/20 rounded-sm text-sm"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-12 bg-stone-900 hover:bg-stone-800 text-white font-medium rounded-sm text-sm tracking-wide transition-all duration-200 group"
              disabled={loading}
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Accesso in corso...</>
              ) : (
                <>Accedi <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" /></>
              )}
            </Button>
          </form>

          <div className="mt-16 pt-6 border-t border-stone-200/60">
            <p className="text-[10px] text-stone-300 font-mono tracking-wider text-center uppercase">
              La Gestione della Tua Casa &middot; Property Manager
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
